import {useCallback, useContext, useEffect, useState} from "react";
import useGetOptionTitle from "@hooks/useGetOptionTitle";
import {getConfigurationKpi} from "@utils/storeConfigurationKpi";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import prepareConfigurationStep from "@utils/prepareConfigurationStep";
import useAsyncError from "@hooks/useAsyncError";
import convertSurveyResponses from "@utils/convertSurveyResponses";
import {getProductIds} from "@utils/storeConfigurationKpi";
import {NotificationsContext} from "@ui-components/Notifications";
import {useIntl} from "react-intl";
import _ from "lodash";
import {
  CrossServicesConfigurationWithIdSchema,
  OptimizationResultSchema,
  ProductsWithIdSchema,
  ServicesConfigurationsWithIdSchema,
  ServiceSurveyResponsesWithIdSchema
} from "../../types";
import {SurveyProductItemSchema, SurveyProductsSchema} from "../types";
import {PDFProductSplitQuantitiesSchema} from "../../worksites/detail/types";
import {
  getRemoteSpreadsheetResults,
  getRemoteKColorResults,
} from "@feature/remote-calculation-results";
import roundToTwoDecimals from "@utils/roundToTwoDecimals";
import useGetServiceConfiguration from "@hooks/useGetServiceConfiguration";
import apiRequest from "../../../services/apiRequest";
import {prepareCrossData} from "../../../utils/prepareCrossData";
import {useCrossServicesConfigurations} from "../../cross_services_configurations/hooks/useCrossServicesConfigurations";


export const useSurveyProducts = (
  keepZeroQuantities = false,
) => {

  // state
  const [loading, setLoading] = useState<boolean>(false);

  // context
  const {push} = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance();
  const getOptionTitle = useGetOptionTitle();
  const throwError = useAsyncError();
  const getServiceConfiguration = useGetServiceConfiguration();
  const {getCrossServicesConfigurations} = useCrossServicesConfigurations()

  // utilities
  const fetchProductsByIds = async (ids: number[]): Promise<ProductsWithIdSchema[]> => {
    const params = {ids: ids.join(",")};
    return await countryApiRequest!.get("/products/all", {params});
  }

  const fetch = useCallback(
    // EVALUATION CONTEXT: WEB, PDF (added in order to evaluate the products with the correct context ticket H2800382902)
    async (
      surveys: any[],
      evaluationContext: string | null = "WEB",
      // k-color products price calculation strategy, ONLY USED
      // when creating a quotation.
      QuotationkColorPriceChoice: "kerakoll" | "tintometer" | null = null
    ) => {
      const result: SurveyProductsSchema = {};
      // useful for k-color products price calculation
      const packagePrices: {[key: string]: number} = {};
      const packages: {[key: string]: number} = {};
      const values: { [key: string]: number } = {};

      setLoading(true);
      try {
        const configurationCache: {
          [key: string]: ServicesConfigurationsWithIdSchema[];
        } = {};
        const productsCache: { [key: number]: ProductsWithIdSchema } = {};

        // for each survey, extract products kpis (product id + total price)
        for (const s of surveys) {
          const configurationsKey = `${s.service_id}_V${s.service_version}`;

          // fetch survey responses and configurations
          const responsesPromise: Promise<
            ServiceSurveyResponsesWithIdSchema[]
          > = countryApiRequest!.get("/service_survey_responses/all", {
            params: { service_survey_id: s.id },
          });
          const configurationsPromise: Promise<
            ServicesConfigurationsWithIdSchema[]
          > =
            configurationsKey in configurationCache
              ? new Promise((resolve) => resolve(configurationCache[configurationsKey]))
              : getServiceConfiguration(s.service_id, s.service_version)
                .then((configurations) => {
                  const parsed = configurations as unknown as ServicesConfigurationsWithIdSchema[]
                  configurationCache[configurationsKey] = parsed;
                  return parsed;
                })
          const kccOptimizationResultsPromise: Promise<OptimizationResultSchema[]> =
            countryApiRequest!.get(`/tintometers/optimization_result/${s.id}`, {params: {skip_tintometer_flg: true}});
          const spreadsheetResultsPromise =
            getRemoteSpreadsheetResults(s.id, countryApiRequest!);
          const crossConfigurationsPromise: Promise<CrossServicesConfigurationWithIdSchema[]> =
            getCrossServicesConfigurations(s.service_id, s.service_version)
          const kColorResultsPromise = getRemoteKColorResults(
            parseInt(s.id),
            countryApiRequest
          );

          const [
            responses,
            configuration,
            crossConfigurations,
            kccOptimizationResults,
            spreadsheetResults,
            // step 2: aggiungere la variabile
            kColorResults,
          ] = await Promise.all([
            responsesPromise,
            configurationsPromise,
            crossConfigurationsPromise,
            kccOptimizationResultsPromise,
            spreadsheetResultsPromise,
            // step 3: aggiungere la variabile
            kColorResultsPromise,
          ]);
          // retrieve kpis
          const data = prepareConfigurationStep(configuration, undefined, s.service_id)
          const crossData = prepareCrossData(crossConfigurations)
          const convertedResponses = convertSurveyResponses(responses, {data, configuration, responses: {}, crossData})

          // if needed, fetch needed products that are not in cache and add them to it
          let neededProductsIds: number[] = _.uniq([
            ...configuration
              .map(({ product_id }) => product_id)
              .filter((v): v is number => !!v),
            ...configuration
              .filter(({ calculation }) => calculation?.length)
              .flatMap(({ calculation }) =>
                getProductIds(calculation, convertedResponses)
              )
              // since getProductIds returns an array, we need to flatten it
              .flatMap((v) => v)
              .filter((v): v is number => typeof v === "number"),
            ...kccOptimizationResults.reduce(
              (acc: number[], curr) => [
                ...acc,
                ...(curr.value as { product_id: number | null }[])
                  .filter(({ product_id }) => !!product_id)
                  .map(({ product_id }) => product_id as number),
              ],
              []
            ),
          ]);

          const kColorProductIds = Object.values(kColorResults).map(
            (el) => el.product_id
          );
          neededProductsIds = _.uniq(
            neededProductsIds.concat(kColorProductIds)
          );

          const unseenProductsIds: number[] = neededProductsIds.filter(
            (pid) => !(pid in productsCache)
          );
          if (unseenProductsIds.length > 0) {
            const localProducts = await fetchProductsByIds(unseenProductsIds);
            localProducts?.forEach((p: any) => (productsCache[p.id] = p));
          }

          const context = {
            survey: s,
            data,
            configuration,
            products: productsCache,
            responses: convertedResponses,
            kccOptimizationResults,
            spreadsheetResults,
            crossData,
            ...(evaluationContext? {context: evaluationContext} : {}),
            kColorResults,
            ...(evaluationContext ? { context: evaluationContext } : {}),
            globalTargetPrice: QuotationkColorPriceChoice,
          };
          // @ts-ignore
          const localKpis: {
            kind: string,
            product_id: string | null,
            value: number,
            info?: SurveyProductItemSchema,
            splitQuantities?: PDFProductSplitQuantitiesSchema,
            kcolor_key?: string,
          }[] = await getConfigurationKpi(apiRequest,context, getOptionTitle, s.name);

          // add survey kpis to global kpis (perform a sum for each product)
          localKpis
            .filter(({ kind }) =>
              ["product", "kcc-product", "kcolor-product"].includes(kind)
            )
            .forEach((kpi) => {
              const {
                kind,
                product_id,
                value,
                info,
                splitQuantities,
                kcolor_key,
              } = kpi;
              let {
                product,
                quantity ,
                kcolor_color_code,
                kcolor_color_rgb,
              } = info || {};
              if (!!product_id) {
                // create product entry if not exists
                const pid = parseInt(product_id);
                if (kind === "kcolor-product") {
                  // should have already been set
                } else {
                  product = productsCache[pid];
                }
                quantity = 0;
              }
              /*
              HUBSPOT TICKET: 2873623725
              if the product is a kcc product and the product contained
              in the kcc response cache has null product_id the quantity is not
              set to zero making it double (has already a quantity) and the
              quantity is also computed and added from line
              result[key].quantity += !!product?.prezzo ? roundToTwoDecimals(value / product.prezzo) : 0;
              */
              if (!product_id && kind === "kcc-product") {
                quantity = 0;
              }
              if (!product) {
                throwError(
                  `Product with id '${product_id}' not found (kind: '${kind}')`
                );
              } else {
                /*
                in case of kcolor product, the key is the kcolor_key
                cod_prod_comm____cod_color____package
                e.g. 119____kk2090____4
                 */
                const key = kind === "kcolor-product" && kcolor_key ?
                  kcolor_key :
                  product.art_cod;

                if (!(key in result)){
                  const isKcc = kind === "kcc-product";
                  const isKcolor = kind === "kcolor-product";
                  result[key] = {
                    product,
                    quantity: quantity!,
                    isKcc,
                    isKcolor,
                    splitQuantities: {} as {
                      [surveyId: number]: PDFProductSplitQuantitiesSchema;
                    },
                    // add kcolor color code and rgb to the product info,
                    // needed to display the color code in the quotation
                    // as a small square colored box next to the product name
                    ...(kcolor_color_code ? {kcolor_color_code} : {}),
                    ...(kcolor_color_rgb ? {kcolor_color_rgb} : {}),
                    ...(isKcolor ? {
                      surveys: new Set<number>([parseInt(s.id)]),
                    }: {}),
                  };
                  // properly initialize kcolor price calculation helpers
                  if (kind === "kcolor-product") {
                    packagePrices[key] = 0;
                    packages[key] = 0;
                    values[key] = 0;
                  }
                }

                if (kind === "kcolor-product") {
                  // add survey to the set of surveys for this product
                  result[key].surveys?.add(parseInt(s.id));
                  // compute totals utiltized for kcolor price calculation
                  packagePrices[key] += product.prezzo_confezione!!
                  packages[key] += product.min_qty!!
                  values[key] += value;
                } else {
                  // update product quantity
                  result[key].quantity += !!product?.prezzo
                    ? roundToTwoDecimals(value / product.prezzo)
                    : 0;
                }
                // update split quantities (if any)
                if (!!splitQuantities)
                  result[key].splitQuantities[s.id] = splitQuantities;
              }
            });
        }
        // remove products with quantity <= 0 (if not explicitly requested to keep them)
        const final = Object.keys(result).reduce((acc, key) => {
          return {
            ...acc,
            [key]: {
              ...result[key],
              /*for kcolor products using kerakoll price, set the unit price accordingly
              this is done because in different surveys, you may have the same:
              - kcolor product
              - color
              - package (e.g. 4L, 10L, etc)
              but different unit prices, since the unit price comes from pricebook table
              which is refreshed on a daily basis by ETL process.
              */
              ...(result[key].isKcolor ? {
                quantity: roundToTwoDecimals(
                  values[key] /
                  (
                    packagePrices[key] / packages[key]
                  )
                ),
              } : {}),
              product: {
                ...result[key].product,
                ...(result[key].isKcolor
                  ? {
                      prezzo: QuotationkColorPriceChoice === "tintometer" ?
                        undefined: packagePrices[key] / packages[key]
                    }
                  : {}),
              },
            }
          }
        }, {}) as SurveyProductsSchema;
        // save results to state filtering zero quantity products if needed
        return Object.keys(final).reduce((acc, key) => {
          if (!keepZeroQuantities && final[key].quantity <= 0)
            return acc;
          return {...acc, [key]: final[key]};
        }, {});
      }
      catch (e) {
        push({'type': 'error', 'title': intl.formatMessage({id: 'an_error_occurred'})});
        throwError(e);
      }
      finally {
        setLoading(false);
      }
    },
    [],  // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {fetch, loading};
}